<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import PaginationLinks from "@/Components/PaginationLinks.vue";
import TextInput from "@/Components/TextInput.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import DialogModal from "@/Components/DialogModal.vue";
import {Inertia} from "@inertiajs/inertia";
import {Link, usePage} from '@inertiajs/inertia-vue3';
import {ref, watch} from "vue";
import Multiselect from "vue-multiselect";
import {useHelpers} from "@/helpers";

const props = defineProps({
  workorders: Object,
  filters: Object,
  isGarmentColorWorkorderView: {
    type: Boolean,
    default: false,
  },
});

const helpers = useHelpers();
const page = usePage();
const currentUser = page.props.value.auth.user;
const closingWorkorder = ref(null);

function openMailPreviewIfPresent() {
  const url = page.props.value?.workorder_mail_preview_url
    ?? page.props.value?.flash?.workorder_mail_preview_url;
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

const formatDate = (timestamp) => {
  if (timestamp <= 0 || !timestamp) {
    return '-';
  }

  return dayjs.tz(timestamp * 1000).format('MM/DD/YYYY');
}
const searching = ref(false);
const defaultFilters = {
  is_open: 'yes',
  status: 'open',
  company: null,
  user: null,
  description: null,
  type: null,
};
const filterValues = ref(Object.assign({}, defaultFilters, props.filters));
watch(() => filterValues.value.status, () => {
  filterValues.value.is_open = (filterValues.value.status === 'open') ? 'yes' : 'no';
  makeSearchRequest();
});
const makeSearchRequest = _.debounce(() => executeSearchRequest(), 400);

const executeSearchRequest = () => {
  searching.value = true;
  const queryParameters = route().params || {};
  if (queryParameters.page) {
    delete queryParameters.page;
  }
  queryParameters['filters'] = filterValues.value;

  Inertia.get(route(route().current(), queryParameters), {}, {
    preserveScroll: true,
    preserveState: true,
    onFinish() {
      searching.value = false;
    }
  });
}

const workorderClasses = (workorder) => {
  if (workorder.open === 'no') {
    return ['hover:bg-gray-200'];
  }
  if (!workorder.completiondate || workorder.completiondate < 0) {
    return ['hover:bg-gray-200'];
  }

  if (dayjs.tz().isAfter(dayjs.tz(workorder.completiondate * 1000))) {
    return ['hover:bg-red-200', 'bg-red-50'];
  }

  return ['hover:bg-gray-200'];
}

const openCloseWorkorderModal = (workorder) => {
  closingWorkorder.value = workorder;
}

const closeCloseWorkorderModal = () => {
  closingWorkorder.value = null;
}

const closeWorkorder = (workorder) => {
  Inertia.post(route('admin.workorders.close', {workorder: workorder.ID}), {}, {
    preserveScroll: true,
    onSuccess: () => openMailPreviewIfPresent(),
    onError: () => alert(`There was an error. Try again later`)
  });
  closingWorkorder.value = null;
}
</script>

<template>
  <AppLayout title="Workorders">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        <span>Workorders</span>
      </h2>
    </template>

    <div class="mx-auto py-10 sm:px-6 lg:px-8">
      <div class="bg-white relative shadow-md sm:rounded-lg overflow-hidden">
        <div
          class="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4 p-4 border-b">
          <div class="w-full flex items-center space-x-3">
          </div>
          <div class="w-full flex flex-row items-center justify-end space-x-3">
            <Link :href="route('admin.workorders.create')">
              <button type="button"
                      class="w-full md:w-auto flex items-center justify-center text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-3 py-2">
                <span class="fa fa-plus text-white font-bold mr-2"></span>
                Create new Workorder
              </button>
            </Link>
          </div>
        </div>
        <div
          class="flex flex-col-reverse md:flex-row items-start md:items-center justify-between md:space-x-4 p-4 border-b">
          <div class="grid grid-cols-1 md:grid-cols-5 w-full md:gap-4">
            <div class="w-full" v-if="isGarmentColorWorkorderView">
              <label for="type" class="sr-only">Type</label>
              <TextInput id="type" placeholder="Search Type" v-model="filterValues.type"
                         @keyup="makeSearchRequest" class="w-full"/>
            </div>
            <div class="w-full">
              <label for="description" class="sr-only">Description</label>
              <TextInput id="description" placeholder="Search Description" v-model="filterValues.description"
                         @keyup="makeSearchRequest" class="w-full"/>
            </div>
            <div class="w-full">
              <label for="company" class="sr-only">Company</label>
              <TextInput id="company" placeholder="Search Company" v-model="filterValues.company"
                         @keyup="makeSearchRequest" class="w-full"/>
            </div>
            <div class="w-full">
              <label for="user" class="sr-only">Assigned User</label>
              <TextInput id="user" placeholder="Search Assigned User" v-model="filterValues.user"
                         @keyup="makeSearchRequest" class="w-full"/>
            </div>
            <div class="w-full">
              <Multiselect id="status" v-model="filterValues.status" :options="['open', 'closed']"
                           placeholder="Status" class="w-full capitalize focus:outline-none"/>
            </div>
          </div>
        </div>
        <div class="overflow-x-auto border-b">
          <table class="w-full text-sm text-left text-gray-500">
            <thead class="text-xs uppercase bg-gray-50">
            <tr>
              <th scope="col" class="px-4 py-3">#</th>
              <th scope="col" class="px-4 py-3">{{ isGarmentColorWorkorderView ? 'Type' : 'Description' }}</th>
              <th v-if="isGarmentColorWorkorderView" scope="col" class="px-4 py-3">Description</th>
              <th scope="col" class="px-4 py-3">Company</th>
              <th scope="col" class="px-4 py-3">Assigned User</th>
              <th scope="col" class="px-4 py-3">Estimated Hours</th>
              <th scope="col" class="px-4 py-3">Assigned At</th>
              <th scope="col" class="px-4 py-3">Completion Date</th>
              <th scope="col" class="px-4 py-3">Closed At</th>
              <th scope="col" class="px-4 py-3">Status</th>
              <th scope="col" class="px-4 py-3">Actions</th>
            </tr>
            </thead>
            <tbody>
            <tr class="cursor-pointer" v-for="workorder in workorders.data"
                :class="workorderClasses(workorder)">
              <td class="px-4 py-3 font-medium text-gray-900 break-words" v-text="workorder.identifier"></td>
              <th scope="row" class="px-4 py-3 font-medium text-gray-900 break-words"
                  v-text="`${workorder.billing_code.description} - $${workorder.billing_code.price}`">
              </th>
              <td v-if="isGarmentColorWorkorderView" class="px-4 py-3 font-medium text-gray-900 break-words"
                  v-text="workorder.description || '-'"></td>
              <th scope="row" class="px-4 py-3 font-medium text-gray-900 break-words"
                  v-text="workorder.company.client">
              </th>
              <td class="px-4 py-3 font-medium text-gray-900 break-words" v-text="workorder.user?.full_name"></td>
              <td class="px-4 py-3 font-medium text-gray-900 break-words text-center" v-text="workorder.hours"></td>
              <td class="px-4 py-3 font-medium text-gray-900 break-words"
                  v-text="formatDate(workorder.dateassigned)"></td>
              <td class="px-4 py-3 font-medium text-gray-900 break-words"
                  v-text="formatDate(workorder.completiondate)"></td>
              <td class="px-4 py-3 font-medium text-gray-900 break-words"
                  v-text="formatDate(workorder.datebilled)"></td>
              <td class="px-4 py-3 break-words">
                <div
                  class="w-fit text-xs font-medium mr-2">
                  <span v-if="workorder.open === 'yes'"
                        class="bg-green-100 text-green-800 px-2.5 py-0.5 rounded">Open</span>
                  <span v-else class="bg-orange-100 text-orange-800 px-2.5 py-0.5 rounded">Closed</span>
                </div>
              </td>
              <td class="px-4 py-3 break-words">
                <Link :href="route('admin.workorders.edit', {workorder: workorder.ID})" class="fa fa-pen p-1"
                      :title="`Edit workorder`">
                </Link>
                <button
                  v-if="workorder.open === 'yes' && helpers.userHasPermission(currentUser, 'admin')"
                  class="fa fa-times p-1"
                  :title="`Close ${workorder.identifier}`"
                  :disabled="closingWorkorder !== null"
                  @click="openCloseWorkorderModal(workorder)">
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="px-4 pt-3 pb-4" aria-label="Table navigation">
          <PaginationLinks :pagination="workorders"/>
        </div>
      </div>
    </div>

    <DialogModal :show="closingWorkorder !== null" @close="closeCloseWorkorderModal">
      <template #title>
        <b>Please confirm</b>
      </template>

      <template #content>
        You are about to close workorder <b v-text="closingWorkorder?.identifier"></b>.
        <br>Are you sure?
      </template>

      <template #footer>
        <SecondaryButton @click="closeCloseWorkorderModal" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="closeWorkorder(closingWorkorder)">
          Confirm
        </PrimaryButton>
      </template>
    </DialogModal>
  </AppLayout>
</template>
